import moment from "moment-timezone";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Checkbox } from "components/designV2/checkbox/checkbox";
import { LabeledValue } from "components/designV2/labeledValue/labeledValue";
import { Tag } from "components/designV2/tag/tag";
import { CustomIcon } from "components/icon/customIcon.component";
import { ColorV2 } from "config/colors";
import type { ApplicationDto } from "model/Application";
import { toApplicationShiftType } from "./applicationList.display.logic";
import {
  ShiftApplicants,
  ShiftApplicantsHeader,
  ShiftCompletion,
  ShiftExpandButton,
  ShiftRowCard,
  ShiftSummary,
  ShiftTypeColumn,
  ShiftTypeLabel,
  ShiftTypeValue,
} from "./applicationList.styled";
import { ApplicationListItem } from "./applicationListItem.component";

interface Props {
  readonly applications: readonly ApplicationDto[];
  readonly handleShowContract: (application: ApplicationDto) => void;
  readonly isPendingView: boolean;
  readonly onApplicationUpdated?: (application: ApplicationDto) => void;
  readonly selectedApplications: readonly ApplicationDto[];
  readonly setSelectedApplications: React.Dispatch<
    React.SetStateAction<ApplicationDto[]>
  >;
}

export const ApplicationShiftRow = (props: Props): JSX.Element | null => {
  const { t, i18n } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);
  const firstApplication = props.applications[0];

  if (!firstApplication) return null;

  const gig = firstApplication.gig;
  const startTime = moment
    .tz(gig.startTime, "Europe/Stockholm")
    .locale(i18n.language);
  const endTime = moment
    .tz(gig.endTime, "Europe/Stockholm")
    .locale(i18n.language);
  const shiftType = toApplicationShiftType(gig.type);
  const shiftTypeLabel =
    shiftType.type === "public"
      ? t("ApplicationsCompany.PublicShift")
      : t("ApplicationsCompany.TeamShift");
  const teamName = gig.structure?.workGroupName?.trim() || "-";
  const hiredWorkerCount = Math.max(
    ...props.applications.map(
      (application) => application.gig.hiredWorkerCount,
    ),
  );
  const isFullyStaffed =
    gig.amountOfWorkers > 0 && hiredWorkerCount >= gig.amountOfWorkers;
  const toggleExpanded = (): void => {
    setIsExpanded((current) => !current);
  };

  const toggleSelectionMode = (checked: boolean): void => {
    setIsSelecting(checked);
    if (checked) return;
    const applicationIds = new Set(
      props.applications.map((application) => application.id),
    );
    props.setSelectedApplications((current) =>
      current.filter((application) => !applicationIds.has(application.id)),
    );
  };

  return (
    <ShiftRowCard>
      <ShiftSummary
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        aria-label={`${t(
          isExpanded
            ? "ApplicationsCompany.CollapseShift"
            : "ApplicationsCompany.ExpandShift",
        )}: ${gig.role || "-"}`}
        onClick={toggleExpanded}
        onKeyDown={(event) => {
          if (event.target !== event.currentTarget) return;
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          toggleExpanded();
        }}
      >
        <LabeledValue
          value={gig.role || "-"}
          label={t("GigDetails.Role")}
          muted={!gig.role}
          truncate
          variant="small"
        />
        <LabeledValue
          value={teamName}
          label={t("General.Workgroup")}
          muted={teamName === "-"}
          truncate
          variant="small"
        />
        <LabeledValue
          value={startTime.isValid() ? startTime.format("D MMMM") : "-"}
          label={t("ApplicationsCompany.Date")}
          muted={!startTime.isValid()}
          variant="small"
        />
        <LabeledValue
          value={
            startTime.isValid() && endTime.isValid()
              ? `${startTime.format("HH:mm")}–${endTime.format("HH:mm")}`
              : "-"
          }
          label={t("ApplicationsCompany.TimeSpan")}
          muted={!startTime.isValid() || !endTime.isValid()}
          variant="small"
        />
        <ShiftTypeColumn>
          <ShiftTypeValue>
            {shiftType.type !== "unsupported" && (
              <CustomIcon
                name={shiftType.iconName}
                size="18px"
                color={ColorV2.Blue1}
              />
            )}
            {shiftTypeLabel}
          </ShiftTypeValue>
          <ShiftTypeLabel>{t("ApplicationsCompany.ShiftType")}</ShiftTypeLabel>
        </ShiftTypeColumn>
        <LabeledValue
          value={`${hiredWorkerCount} / ${gig.amountOfWorkers}`}
          label={t("ApplicationsCompany.HiredPlural")}
          variant="small"
        />
        <ShiftCompletion>
          {isFullyStaffed ? (
            <Tag
              backgroundColor={ColorV2.PositiveGreenLight}
              text={t("ApplicationsCompany.FullyStaffed")}
              textColor={ColorV2.SimflexityGreen}
            />
          ) : (
            <Tag
              backgroundColor={ColorV2.UnplannedAbsenceYellow}
              text={t("ApplicationsCompany.OpenPositions")}
              textColor={ColorV2.DarkAmbigiousYellow}
            />
          )}
        </ShiftCompletion>
        <ShiftExpandButton
          type="button"
          aria-expanded={isExpanded}
          aria-label={t(
            isExpanded
              ? "ApplicationsCompany.CollapseShift"
              : "ApplicationsCompany.ExpandShift",
          )}
          onClick={(event) => {
            event.stopPropagation();
            toggleExpanded();
          }}
        >
          <CustomIcon
            name={isExpanded ? "v2/up-chevron" : "v2/down-chevron"}
            size="20px"
            color={ColorV2.Blue1}
          />
        </ShiftExpandButton>
      </ShiftSummary>

      {isExpanded && (
        <ShiftApplicants>
          {props.isPendingView && (
            <ShiftApplicantsHeader>
              <Checkbox
                label={t("ApplicationsCompany.SelectMultiple")}
                checked={isSelecting ? "checked" : "unchecked"}
                onChange={toggleSelectionMode}
              />
            </ShiftApplicantsHeader>
          )}
          {props.applications.map((application) => {
            const showContract =
              application.worker.employmentType === "SimflexityEmployee";
            return (
              <ApplicationListItem
                key={application.id}
                application={application}
                handleShowContract={props.handleShowContract}
                onApplicationUpdated={props.onApplicationUpdated}
                selectedApplications={props.selectedApplications}
                setSelectedApplications={props.setSelectedApplications}
                showContract={showContract}
                showSelection={props.isPendingView && isSelecting}
              />
            );
          })}
        </ShiftApplicants>
      )}
    </ShiftRowCard>
  );
};
